import { Action } from "redux";

import { ActionType, TriggerType } from "./enums";
import { IAction, IDialogCanceledAction, IDialogConfirmedAction, ITrigger } from "./types";
import { UiWorkflow } from "./UiWorkflow";
import { WorkflowManager } from "./WorkflowManager";
import { Text } from "./workflowModel/Triggers";
import {
    CONST_ATTRIBUTE_DATA_CHANGE_ATTR_NAME_AT,
    CONST_ATTRIBUTE_DATA_CHANGE_CONDITION_AT,
    CONST_CALL_METHOD_DEFAULT_PARAM_VALUE_AT,
    CONST_CALL_METHOD_METHOD_NAME_AT,
    CONST_CALL_SCRIPT_SCRIPT_AT,
    CONST_INCREMENT_NUMBER_ATTR_NAME_AT,
    CONST_INCREMENT_NUMBER_VALUE_AT,
    CONST_MENU_CAPTIONS_EL,
    CONST_MENU_CODE_NAME,
    CONST_MENU_IDENTIFIER_AT,
    CONST_OPEN_FORM_FORM_TYPE_AT,
    CONST_OPEN_FORM_MASTER_LINK_TO_ATTR_AT,
    CONST_SEND_MAIL_RECIPIENT_AT,
    CONST_SEND_MAIL_RECIPIENT_TYPE_AT,
    CONST_SEND_MAIL_SUBJECT_AT,
    CONST_SEND_SMS_RECIPIENT_AT,
    CONST_SEND_SMS_RECIPIENT_TYPE_AT,
    CONST_SEND_WF_MESSAGE_MESSAGE_NAME_AT,
    CONST_SEND_WF_MESSAGE_TARGET_OBJECT_NAME_AT,
    CONST_SET_ATTRIBUTE_ATTR_NAME_AT,
    CONST_SET_ATTRIBUTE_VALUE_EL,
    CONST_SET_OBJECT_ATTRIBUTE_ATTR_NAME_AT,
    CONST_SET_OBJECT_ATTRIBUTE_VALUE_EL,
    CONST_SET_VALUE_NAME_AT,
    CONST_SET_VALUE_VALUE_EL,
    CONST_WFMESSAGE_MESSAGE_NAME_AT,
} from "./workflowParser/constants";

/**
 * Funkce přeparsuje string na number.
 * @example "" => defaultReturnValue
 * @example "a" => defaultReturnValue
 * @example "1" => 1
 * @example "0a" => defaultReturnValue
 * @param value Textový řetězec k parsování.
 * @param defaultReturnValue defaultní návratová hodnota, která se má vrátit, pokud parsování neprojde.
 */
export const string2number = (value: string, defaultReturnValue: number) => {
    if (value === "")
        return defaultReturnValue;

    const number = Number(value);

    return isNaN(number) ? defaultReturnValue : number;
};

/**
 * Funkce přeparsuje textový řetězec na boolean. Pokud se parsování nezdaří, vrátí false.
 * @param input Textový řetězec k přeparsování na boolean.
 * @param defaultBoolean Defaultní návratová hodnota, pokud input se nerovná true.
 */
export const parseStringToBoolean = (input: string | null, defaultBoolean: boolean): boolean => {
    return input ? input.toLowerCase() === "true" : defaultBoolean;
};

/**
 * Funkce vytváří název pro daný typ akce.
 * @param action Akce, pro kterou se má vytvořit jméno.
 */
const getActionName = (action: IAction) => {
    const { actionType, properties } = action;

    switch (actionType) {
        case ActionType.CallMethod:
            return concatenateParameters([properties[CONST_CALL_METHOD_METHOD_NAME_AT], properties[CONST_CALL_METHOD_DEFAULT_PARAM_VALUE_AT]]);
        case ActionType.CallScript:
            return properties[CONST_CALL_SCRIPT_SCRIPT_AT];
        case ActionType.IncrementValue:
            return concatenateParameters([properties[CONST_INCREMENT_NUMBER_ATTR_NAME_AT], properties[CONST_INCREMENT_NUMBER_VALUE_AT]]);
        case ActionType.SendMail:
            return concatenateParameters([properties[CONST_SEND_MAIL_RECIPIENT_TYPE_AT], properties[CONST_SEND_MAIL_RECIPIENT_AT], properties[CONST_SEND_MAIL_SUBJECT_AT]]);
        case ActionType.SendSMS:
            return concatenateParameters([properties[CONST_SEND_SMS_RECIPIENT_TYPE_AT], properties[CONST_SEND_SMS_RECIPIENT_AT]]);
        case ActionType.SendWFMessage:
            return concatenateParameters([properties[CONST_SEND_WF_MESSAGE_MESSAGE_NAME_AT], properties[CONST_SEND_WF_MESSAGE_TARGET_OBJECT_NAME_AT]]);
        case ActionType.SetAttribute:
            return concatenateParameters([properties[CONST_SET_ATTRIBUTE_ATTR_NAME_AT], properties[CONST_SET_ATTRIBUTE_VALUE_EL]], "=");
        case ActionType.SetObjectAttribute:
            return concatenateParameters([properties[CONST_SET_OBJECT_ATTRIBUTE_ATTR_NAME_AT], properties[CONST_SET_OBJECT_ATTRIBUTE_VALUE_EL]]);
        case ActionType.SetValue:
            return concatenateParameters([properties[CONST_SET_VALUE_NAME_AT], properties[CONST_SET_VALUE_VALUE_EL]]);
        case ActionType.OpenForm:
            return concatenateParameters([properties[CONST_OPEN_FORM_FORM_TYPE_AT], properties[CONST_OPEN_FORM_MASTER_LINK_TO_ATTR_AT]]);
        default:
            throw new Error(`Action type '${actionType}' is not supported, please check the implementation.`);
    }
};

/**
 * Funkce vytváří název pro daný typ spouštěče.
 * @param trigger Spouštěč, pro který se má vytvořit jméno.
 */
const getTriggerName = (trigger: ITrigger) => {
    const { triggerType, properties } = trigger;

    switch (triggerType) {
        case TriggerType.AttributeDataChange:
            return concatenateParameters([properties[CONST_ATTRIBUTE_DATA_CHANGE_ATTR_NAME_AT], properties[CONST_ATTRIBUTE_DATA_CHANGE_CONDITION_AT]]);
        case TriggerType.Menu:
            return getMenuName(properties);
        case TriggerType.NewObject:
            return "";
        case TriggerType.WfMessage:
            return properties[CONST_WFMESSAGE_MESSAGE_NAME_AT];
        default:
            throw new Error(`Trigger type '${triggerType}' is not supported, please check the implementation.`);
    }
};

/**
 * Vrací jméno triggeru Menu.
 * @param properties Properties pro trigger.
 */
const getMenuName = (properties: ITrigger["properties"]) => {
    const unparsedCaptions = properties[CONST_MENU_CAPTIONS_EL];
    const codename = properties[CONST_MENU_CODE_NAME];
    const captions: Text[] = unparsedCaptions == null ? [] : JSON.parse(unparsedCaptions);
    const czCaption = captions.find(cap => cap.lang === "CZ"); // Do budoucna bude potřeba zjišťovat aktuální jazyk aplikace a podle něho zobrazit caption

    let caption = czCaption?.text ?? captions[0]?.text ?? "";

    if (codename !== "")
        caption = `${caption} - ${codename}`;

    return `${caption} ${properties[CONST_MENU_IDENTIFIER_AT]}`;
};

/**
 * Funkce sloužící pro zřetězení hodnot parametrů do stringu ve tvaru {param1} / {param2} / ... / {paramN}
 * @param parameters
 * @param delimiter
 */
const concatenateParameters = (parameters: string[], delimiter: string = " / ") => {
    let concatenatedString = "";

    parameters.forEach((parameter, index) => {
        if (index === 0)
            concatenatedString = parameter;
        else if (parameter)
            concatenatedString += `${concatenatedString ? delimiter : ""}${parameter}`;
    });

    return concatenatedString;
};

export const updateActions = (newActions: readonly IAction[], oldActions: readonly IAction[] = []) => {
    const updatedActions = [...newActions];

    for (let i = 0; i < newActions.length; i++) {
        const newAction = newActions[i];

        if (newAction !== oldActions[i])
            updatedActions[i] = { ...newAction, name: getActionName(newAction) };
    }

    return updatedActions;
};

export const updateTriggers = (newTriggers: readonly ITrigger[], oldTriggers: readonly ITrigger[] = []) => {
    const updatedTriggers = [...newTriggers];

    for (let i = 0; i < newTriggers.length; i++) {
        const newTrigger = newTriggers[i];

        if (newTrigger !== oldTriggers[i])
            updatedTriggers[i] = { ...newTrigger, name: getTriggerName(newTrigger) };
    }

    return updatedTriggers;
};

/**
 * Vrací mutable objekt reprezentující aktuální workflow.
 * @param hostKey Klíč instance editoru WF.
 */
export const getUiWorkflowMutableObject = (hostKey: string): UiWorkflow => {
    return WorkflowManager.uiWorkflows[hostKey];
};

/**
 * Určuje, jestli se jedná o správnou akci z dialogového okna se správným ID akce.
 * @param action redux akce
 * @param actionId ID akce na kterou dal uživatel odpověď.
 */
export const createDialogUserResponsePattern = (actionId: string) => (action: Action) => {
    if (action instanceof IDialogCanceledAction)
        return actionId === action.payload.actionId;

    if (action instanceof IDialogConfirmedAction)
        return actionId === action.payload.actionId;

    return false;
};
